import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    List,
    ListItem,
    ListItemText,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import TaskTrackingService, { taskErrorMessage } from '../../services/TaskTrackingService';
import { bulkBody, CONTENT_TYPE_FOR, languagesInFiles } from './startTracking';
import { contentFieldsError } from './taskFields';
import TaskFieldsSection, { emptyTaskFields } from './TaskFieldsSection';
import { shortName } from './taskUi';
import { refreshTaskSummary } from './useTaskSummary';

const STATUS_TEXT = {
    WILL_CREATE: 'will start',
    ALREADY_OPEN: 'already tracked',
    NOT_FOUND: 'file not found',
    UNKNOWN_LANGUAGE: 'unknown language',
    CREATED: 'started',
};

/**
 * Bulk "Start tracking" for files selected in the Files browser. Step 1 picks languages, PM and
 * dates; step 2 is a dry run showing what will happen (and who needs access); then commit.
 */
const StartTrackingDialog = ({ open, onClose, files, onDone }) => {
    useTrackUsage('StartTrackingDialog', 'dialog', 'tasks', open);
    const [fields, setFields] = useState(emptyTaskFields());
    const [preview, setPreview] = useState(null);
    const [grant, setGrant] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    // What the one file already has from another language; shown read only
    const [existing, setExisting] = useState(null);

    const items = (files || [])
        .filter((f) => CONTENT_TYPE_FOR[f.type] && f.data)
        .map((f) => ({ contentType: CONTENT_TYPE_FOR[f.type], contentId: f.data }));

    useEffect(() => {
        if (!open) return undefined;
        setFields({ ...emptyTaskFields(), track: true });
        setPreview(null);
        setResult(null);
        setError(null);
        setGrant(false);
        setExisting(null);
        if (items.length !== 1) return undefined;
        let cancelled = false;
        const [item] = items;
        // A video with no tasks yet can still have the link it was added from
        const videoLink =
            item.contentType === 'VIDEO' ? TaskTrackingService.videoSourceLink(item.contentId).catch(() => null) : null;
        Promise.all([TaskTrackingService.forContents(items), videoLink])
            .then(([rows, link]) => {
                const row = rows.find((r) => r.categoryKey || r.durationSeconds || r.sourceUrl || r.platform);
                const sourceUrl = row?.sourceUrl || link;
                if (cancelled || (!row && !sourceUrl)) return;
                setExisting({
                    title: row?.contentTitle || files[0].videoName || files[0].name || 'this file',
                    categoryKey: row?.categoryKey,
                    durationSeconds: row?.durationSeconds,
                    sourceUrl,
                    platform: row?.platform,
                });
                // The box is gone now, so nothing typed in it may go out
                if (sourceUrl) setFields((f) => ({ ...f, sourceUrl: '' }));
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const run = async (dryRun) => {
        setBusy(true);
        setError(null);
        try {
            const res = await TaskTrackingService.startBulk(bulkBody(items, fields, { dryRun, grantContentAccess: grant }));
            if (dryRun) {
                setPreview(res);
            } else {
                setResult(res);
                refreshTaskSummary();
                onDone && onDone(res);
            }
        } catch (e) {
            setError(taskErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const presentLanguages = languagesInFiles(files);
    const shown = result || preview;
    const willCreate = preview ? preview.items.filter((i) => i.status === 'WILL_CREATE').length : 0;
    const needsAccess = preview?.accessNeeded?.length > 0;

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Start tracking {items.length} file{items.length === 1 ? '' : 's'}</DialogTitle>
            <DialogContent>
                {!shown && (
                    <TaskFieldsSection
                        value={fields}
                        onChange={setFields}
                        contentType={items[0]?.contentType}
                        languageOptions={presentLanguages}
                        singleFile={items.length === 1}
                        existing={existing}
                        bulk={items.length > 1}
                        alwaysOn
                    />
                )}
                {shown && (
                    <>
                        <List dense sx={{ maxHeight: 280, overflowY: 'auto' }}>
                            {shown.items.map((i, n) => (
                                <ListItem key={n} disableGutters>
                                    <ListItemText
                                        primary={`${i.title || i.contentId} · ${i.language}`}
                                        secondary={STATUS_TEXT[i.status] || i.message || i.status}
                                    />
                                </ListItem>
                            ))}
                        </List>
                        {!result && needsAccess && (
                            <Box sx={{ mt: 1 }}>
                                <Alert severity="warning">
                                    These people can't open some of the files yet:{' '}
                                    {preview.accessNeeded.map((a) => `${shortName(a.user)} (${a.title || a.contentId})`).join(', ')}
                                </Alert>
                                <FormControlLabel
                                    control={<Checkbox checked={grant} onChange={(e) => setGrant(e.target.checked)} />}
                                    label="Give them edit access to the folders holding these files"
                                />
                            </Box>
                        )}
                        {result && (
                            <Typography sx={{ mt: 1 }}>
                                Started {result.items.filter((i) => i.status === 'CREATED').length} task(s).
                            </Typography>
                        )}
                    </>
                )}
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                {result ? (
                    <Button variant="contained" onClick={onClose}>
                        Done
                    </Button>
                ) : (
                    <>
                        <Button onClick={preview ? () => setPreview(null) : onClose} disabled={busy}>
                            {preview ? 'Back' : 'Cancel'}
                        </Button>
                        {!preview ? (
                            <Button
                                variant="contained"
                                disabled={busy || fields.languages.length === 0 || items.length === 0 || !!contentFieldsError(fields)}
                                onClick={() => run(true)}
                            >
                                Review
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                disabled={busy || willCreate === 0 || (needsAccess && !grant)}
                                onClick={() => run(false)}
                            >
                                Start {willCreate} task{willCreate === 1 ? '' : 's'}
                            </Button>
                        )}
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default StartTrackingDialog;
